import axios from 'axios';
import { message } from 'antd';

// Request deduplication cache
const pendingRequests = new Map();

// Determine base URL from runtime config or environment
const getBaseURL = () => {
  const runtimeURL = window.ENV?.REACT_APP_API_BASE_URL;
  const envURL = process.env.REACT_APP_API_BASE_URL;
  
  // Skip if runtime URL is still a placeholder
  return (runtimeURL && !runtimeURL.includes('PLACEHOLDER')) 
    ? runtimeURL 
    : (envURL || 'http://localhost:3001');
};

// Create axios instance
const apiClient = axios.create({
  baseURL: getBaseURL(),
  timeout: parseInt(window.ENV?.REACT_APP_API_TIMEOUT || process.env.REACT_APP_API_TIMEOUT) || 10000,
  withCredentials: true, // Important for session-based auth
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to generate request key for deduplication
const generateRequestKey = (config) => {
  return `${config.method?.toUpperCase()}_${config.url}_${JSON.stringify(config.params || {})}_${JSON.stringify(config.data || {})}`;
};

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Generate request key for deduplication
    const requestKey = generateRequestKey(config);

    // Check if the same request is already pending
    if (pendingRequests.has(requestKey)) {
      // Return the existing promise instead of making a new request
      return pendingRequests.get(requestKey);
    }

    // Add request timestamp for debugging
    config.metadata = { startTime: new Date(), requestKey };

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Clean up pending request from deduplication cache
    if (response.config.metadata?.requestKey) {
      pendingRequests.delete(response.config.metadata.requestKey);
    }

    return response;
  },
  (error) => {
    // Clean up pending request from deduplication cache
    if (error.config?.metadata?.requestKey) {
      pendingRequests.delete(error.config.metadata.requestKey);
    }

    // Handle different error scenarios
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          // Unauthorized - redirect to login
          if (window.location.pathname !== '/login') {
            message.error('Session expired. Please log in again.');
            window.location.href = '/login';
          }
          break;
          
        case 403:
          // Forbidden
          message.error('You do not have permission to perform this action.');
          break;
          
        case 404:
          // Not found
          message.error('The requested resource was not found.');
          break;
          
        case 422:
          // Validation error
          if (data.errors && Array.isArray(data.errors)) {
            data.errors.forEach(err => message.error(err.message || err));
          } else {
            message.error(data.message || 'Validation error occurred.');
          }
          break;
          
        case 429:
          // Rate limit exceeded
          message.error('Too many requests. Please try again later.');
          break;
          
        case 500:
          // Server error - show specific message if available
          message.error(data.message || 'Server error occurred. Please try again later.');
          break;

        default:
          // Other errors - show specific message from backend
          message.error(data.message || 'An unexpected error occurred.');
      }
      
      // Return structured error
      return Promise.reject({
        status,
        message: data.message || 'An error occurred',
        errors: data.errors || [],
        data: data,
      });
    } else if (error.request) {
      // Network error
      message.error('Network error. Please check your connection.');
      return Promise.reject({
        status: 0,
        message: 'Network error',
        errors: [],
      });
    } else {
      // Other error
      message.error('An unexpected error occurred.');
      return Promise.reject({
        status: 0,
        message: error.message || 'An unexpected error occurred',
        errors: [],
      });
    }
  }
);

// Helper methods for common HTTP operations
export const api = {
  get: (url, config) => apiClient.get(url, config),
  post: (url, data, config) => apiClient.post(url, data, config),
  put: (url, data, config) => apiClient.put(url, data, config),
  patch: (url, data, config) => apiClient.patch(url, data, config),
  delete: (url, config) => apiClient.delete(url, config),
};

export { apiClient };
export default apiClient;
